import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Getting the CSS out of rendered React components",
  "description": "",
  "published": "2019-06-03T00:00:00.000Z",
  "redirect_from": ["/blog/getting-the-css-out-of-rendered-react-components/swizec/9108"],
  "categories": "Front End, Technical",
  "hero": "./img/techletter.app-screenshot-1559573006863.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Here's a fun problem for ya: How do you get the CSS from a rendered React component?`}</p>
    <p>{`Right-click, inspect element, see it in dev tools. D'oh 🙄`}</p>
    <p>{`What about programmatically?`}</p>
    <p><img parentName="p" {...{
        "src": "/6fe9cf9b08f507e71e24c800f745325d/media-kPtv3UIPrv36cjxqLs-giphy.gif",
        "alt": "thinking giphy"
      }}></img></p>
    <p>{`Turns out that's not so easy.`}</p>
    <p>{`You could, for example, try the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle"
      }}><inlineCode parentName="a">{`window.getComputedStyle`}</inlineCode></a>{` method. Same as that DevTools view with all the styles.`}</p>
    <p><img parentName="p" {...{
        "src": "/0dc6248c8a119a904524ad1bd1fef154/JQoQAck.gif",
        "alt": null
      }}></img></p>
    <p>{`But that's too much stuff. You get values for every CSS property that exists in the spec. 😅`}</p>
    <p>{`What else can you try?`}</p>
    <p>{`Well there's always the `}<inlineCode parentName="p">{`style`}</inlineCode>{` prop. You could get its value and hope for the best.`}</p>
    <p>{`But that only works if you're actually using it. Most people these days do not. Sure it's plenty useful for one-off overrides, but when was the last time you saw a project that heavily relies on the style prop?`}</p>
    <p>{`Exactly. Not in a while.`}</p>
    <p>{`Most projects have settled on styled-components or CSS modules. That makes our job both easier and harder. 🤨`}</p>
    <h2 {...{
      "id": "getting-css-from-styled-components"
    }}>{`Getting CSS from styled-components`}</h2>
    <p>{`You can watch me figure this one out in a livestream 👇`}</p>
    <lite-youtube {...{
      "videoid": "v-akMfXZZNE",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`I tried all of the above and then some. Here's the solution I came up with: `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/component-css-extractor"
      }}>{`👨‍🎨 component-css-extractor`}</a>{`. Open sourced for your ease of use 😊`}</p>
    <p>{`CodeSandbox to prove it works 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/heuristic-leakey-b645b?fontsize=14",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/component-css-extractor"
      }}>{`👨‍🎨 component-css-extractor`}</a>{` relies on the fact that both styled-components and CSS modules use class names.`}</p>
    <p>{`Each styled component that you write is assigned a unique class name. Rendered components get the class property, CSS rules go in a `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` tag in your header.`}</p>
    <p>{`We can combine those to get a clean set of CSS rules. Scoped to the target component with nothing extra to mess us up. 👌`}</p>
    <p>{`34 lines of prettified code in total 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=javascript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=import%20css%20from%20%22css%22%3B%0A%0Afunction%20getClassNames(node)%20%7B%0A%20%20%20%20return%20%5B%0A%20%20%20%20%20%20%20%20node.className%2C%0A%20%20%20%20%20%20%20%20...Array.from(node.children).map(getClassNames)%0A%20%20%20%20%5D.flat()%3B%0A%7D%0A%0Aexport%20default%20function%20extractCSS(node)%20%7B%0A%20%20%20%20%2F%2F%20Collect%20class%20names%20for%20a%20DOM%20subtree%0A%20%20%20%20%2F%2F%20Works%20for%20styled-components%20and%20CSS%20modules%20(anything%20based%20on%20CSS%20classes)%0A%20%20%20%20const%20classNames%20%3D%20getClassNames(node)%0A%20%20%20%20%20%20%20%20.map(name%20%3D%3E%20name.split(%22%20%22))%0A%20%20%20%20%20%20%20%20.flat()%0A%20%20%20%20%20%20%20%20.map(name%20%3D%3E%20%60.%24%7Bname%7D%60)%3B%0A%0A%20%20%20%20%2F%2F%20Gets%20embedded%20CSS%20for%20the%20entire%20page%0A%20%20%20%20const%20cssStyles%20%3D%20Array.from(document.head.getElementsByTagName(%22style%22))%0A%20%20%20%20%20%20%20%20.map(style%20%3D%3E%20style.innerHTML)%0A%20%20%20%20%20%20%20%20.join(%22%22)%3B%0A%0A%20%20%20%20%2F%2F%20Filters%20CSS%20for%20our%20classes%0A%20%20%20%20const%20parsedCSS%20%3D%20css.parse(cssStyles)%3B%0A%20%20%20%20parsedCSS.stylesheet.rules%20%3D%20parsedCSS.stylesheet.rules%0A%20%20%20%20%20%20%20%20.filter(rule%20%3D%3E%20rule.type%20%3D%3D%3D%20%22rule%22)%0A%20%20%20%20%20%20%20%20.filter(rule%20%3D%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20rule.selectors.some(selector%20%3D%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20classNames.some(name%20%3D%3E%20name%20%3D%3D%3D%20selector)%0A%20%20%20%20%20%20%20%20%20%20%20%20)%0A%20%20%20%20%20%20%20%20)%3B%0A%0A%20%20%20%20return%20css.stringify(parsedCSS)%3B%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "758px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "103.13901345291481%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'412\\'%20viewBox=\\'0%200%20400%20412\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M18%2026l-1%20180c0%20171%200%20180%202%20181h362c3-1%203-360%200-361H18\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6901ff0efddd990cce74f3519b66742a/ca0a1/techletter.app-screenshot-1559573006863.webp 223w", "/static/6901ff0efddd990cce74f3519b66742a/75680/techletter.app-screenshot-1559573006863.webp 445w", "/static/6901ff0efddd990cce74f3519b66742a/c4e8e/techletter.app-screenshot-1559573006863.webp 758w"],
                "sizes": "(max-width: 758px) 100vw, 758px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6901ff0efddd990cce74f3519b66742a/e92b6/techletter.app-screenshot-1559573006863.png 223w", "/static/6901ff0efddd990cce74f3519b66742a/e66bf/techletter.app-screenshot-1559573006863.png 445w", "/static/6901ff0efddd990cce74f3519b66742a/c8e86/techletter.app-screenshot-1559573006863.png 758w"],
                "sizes": "(max-width: 758px) 100vw, 758px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6901ff0efddd990cce74f3519b66742a/c8e86/techletter.app-screenshot-1559573006863.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`As the comments say:`}</p>
    <ol>
      <li parentName="ol">{`Collect class names for entire subtree`}</li>
      <li parentName="ol">{`Use `}<inlineCode parentName="li">{`document.head.getElementsByTagName("style")`}</inlineCode>{` to get all `}<inlineCode parentName="li">{`<style>`}</inlineCode>{` tags`}</li>
      <li parentName="ol">{`Use the `}<a parentName="li" {...{
          "href": "https://github.com/reworkcss/css"
        }}><inlineCode parentName="a">{`css`}</inlineCode></a>{` module to parse collected CSS into a JavaScript object`}</li>
      <li parentName="ol">{`Filter rules that don't apply to our classes`}</li>
      <li parentName="ol">{`Stringify and return`}</li>
    </ol>
    <p>{`Works like a charm 👨‍🎨`}</p>
    <p><img parentName="p" {...{
        "src": "/9a021a6cd72cfaab539f3b1fef648ed9/media-12NUbkX6p4xOO4-giphy.gif",
        "alt": "magic giphy"
      }}></img></p>
    <h2 {...{
      "id": "some-caveats"
    }}>{`Some caveats`}</h2>
    <p>{`As always there are some limitations.`}</p>
    <p>{`First of all, this won't work on the server or during build time. Collecting `}<inlineCode parentName="p">{`<style>`}</inlineCode>{` tags requires access to the DOM as it exists in the browser. You don't have that during server-side-rendering, I think.`}</p>
    <p>{`Fortunately you don't need `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/component-css-extractor"
      }}>{`👨‍🎨 component-css-extractor`}</a>{` on the server. Styled-components has a built-in way of extracting styles into a string for serving to the client.`}</p>
    <p>{`The built-in way doesn't work in the browser, hence why I went down this rabbit hole.`}</p>
    <p>{`Second of all, if you have global stylesheets in a `}<inlineCode parentName="p">{`.css`}</inlineCode>{` file, `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/component-css-extractor"
      }}>{`👨‍🎨 component-css-extractor`}</a>{` won't pick those up. Makes the code simpler.`}</p>
    <p>{`Imagine having to parse all of CSS in all your stylesheets and figuring out what does and doesn't apply to a particular DOM node. Might as well build a browser rendering engine lol.`}</p>
    <p>{`Happy Monday, ~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      